import { useTranslation } from 'react-i18next';
import { Unlink, ArrowRight } from 'lucide-react';
import type { MediaBreakMap, MediaBreakTransition } from '@bliks-io/contracts';
import { Card, CardHeader, CardTitle, CardContent, cn } from '@bliks-io/ui';
import { getLogoUrl } from './graph-3d-utils';

interface MediaBreakMapCardProps {
  mediaBreakMap: MediaBreakMap;
}

/**
 * Stat box for displaying break counts
 */
function BreakStatBox({
  label,
  value,
  className
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg p-4',
        'bg-danger-surface',
        className
      )}
    >
      <span className="text-3xl font-bold text-danger">{value}</span>
      <span className="mt-1 text-xs font-semibold uppercase tracking-wider text-danger">
        {label}
      </span>
    </div>
  );
}

/**
 * Single transition row showing system-to-system break
 */
function BreakTransitionRow({ transition }: { transition: MediaBreakTransition }) {
  const { t } = useTranslation();
  const fromLogoUrl = getLogoUrl(transition.fromSystem.logo);
  const toLogoUrl = getLogoUrl(transition.toSystem.logo);

  return (
    <div className="flex flex-col gap-1 py-2 px-3 rounded-md hover:bg-muted/50">
      {/* Systems with arrow */}
      <div className="flex items-center gap-2">
        {/* From System */}
        <div className="flex items-center gap-1.5 min-w-0">
          {fromLogoUrl ? (
            <img
              src={fromLogoUrl}
              alt={transition.fromSystem.name}
              className="w-5 h-5 object-contain rounded-xs"
            />
          ) : (
            <div className="w-5 h-5 rounded-xs bg-muted flex items-center justify-center">
              <span className="text-xs font-medium text-muted-foreground">
                {transition.fromSystem.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="text-sm font-medium truncate">{transition.fromSystem.name}</span>
        </div>

        {/* Arrow */}
        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />

        {/* To System */}
        <div className="flex items-center gap-1.5 min-w-0">
          {toLogoUrl ? (
            <img
              src={toLogoUrl}
              alt={transition.toSystem.name}
              className="w-5 h-5 object-contain rounded-xs"
            />
          ) : (
            <div className="w-5 h-5 rounded-xs bg-muted flex items-center justify-center">
              <span className="text-xs font-medium text-muted-foreground">
                {transition.toSystem.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="text-sm font-medium truncate">{transition.toSystem.name}</span>
        </div>
      </div>

      {/* Process count */}
      <span className="text-xs text-muted-foreground">
        {t('organization.systems.mediaBreakMap.foundInProcesses', {
          count: transition.processCount
        })}
      </span>
    </div>
  );
}

export function MediaBreakMapCard({ mediaBreakMap }: MediaBreakMapCardProps) {
  const { t } = useTranslation();

  const hasMostFrequent = mediaBreakMap.mostFrequentBreaks.length > 0;

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Unlink className="w-5 h-5" />
          {t('organization.systems.mediaBreakMap.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Stat boxes */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <BreakStatBox
            label={t('organization.systems.mediaBreakMap.breaksBefore')}
            value={mediaBreakMap.breaksBefore}
            className="min-w-0"
          />
          <BreakStatBox
            label={t('organization.systems.mediaBreakMap.breaksAfter')}
            value={mediaBreakMap.breaksAfter}
            className="min-w-0"
          />
        </div>

        {/* Most frequent breaks section */}
        {hasMostFrequent && (
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              {t('organization.systems.mediaBreakMap.mostFrequent')}
            </h4>
            <div className="space-y-1">
              {mediaBreakMap.mostFrequentBreaks.map((transition, index) => (
                <BreakTransitionRow
                  key={`${transition.fromSystem._id}-${transition.toSystem._id}-${index}`}
                  transition={transition}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
